import {
    AnyAction,
    DocumentSnapshotDescriptor,
    getMatchingCollectionChangesFromPatch,
    isDocumentAction,
} from "@buildwithflux/core";
import {isSystemPart} from "@buildwithflux/models";
import {areWebWorkersSupported, areWeInStorybook, isDevEnv, isTestEnv} from "@buildwithflux/shared";
import {FluxServices} from "export";
import {compact, concat} from "lodash";

import {enqueueSyncErrorNotification} from "modules/document/effects/helpers";
import {bakeNodesFromLatestPatchWithRetry} from "modules/document/effects/sideEffects/bakeNodesFromLatestPatch";
import {isBatchAction} from "modules/document/state/helpers/batchActions";
import {applyPcbLayoutEnginePatches} from "modules/pcb_layout_engine/pcbLayoutEngineInMain";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

import {
    NotifyActionBakeObserverSubscribersPayload,
    PatchHandlerSideEffect,
    PatchHandlerSideEffectWithServices,
} from "./types";

// This represents a queue of baking tasks to ensure that bakings are run sequentially. The primary purpose
// is to enforce sequential, ordered baking as updates (patches). Baking is an async process, where as patches
// arrive in a synchronous manner. Without this queue, it is possible for a later patch to finish baking before
// an earlier patch, resulting in out-of-order state. Note that the queue is self draining - tasks are auto removed
// as the next task begins or, if no new tasks are added, the queue simply waits for next task in an 'idle' resolved
// state.
let bakingQueue = Promise.resolve();

function isPcbControlledBakingEnabled(services: FluxServices) {
    // Effectively disable pcb controlled baking in test environment. This is to avoid
    // a larger refactor that'd inolve updating the test suite. That test suite refactor
    // should be done if/when the feature flag is decommissioned / the bakingQueue becomes
    // a permanent system feature
    if (isTestEnv()) {
        return false;
    }

    return services.useFeatureFlagsStore.getState().enablePcbControlledBaking;
}

/**
 * Runtime safety check to go with makeGenerateActionRecordsEpic
 * TODO: this is still being triggered by some actions like setPartOwners and
 * removeNotification that should not be affecting document state. Need to
 * investigate. See
 * https://buildwithflux.sentry.io/issues/?project=5669122&query=is%3Aunresolved+issue.priority%3A%5Bhigh%2C+medium%5D+patchHandlerEpicSideEffects.ts&referrer=issue-list&statsPeriod=90d
 */
export const runtimeChecks: PatchHandlerSideEffect = ({action, patch}) => {
    const paths = patch.forward.flat().map((p) => p.path);
    if (!paths.length) {
        return;
    }

    if (isBatchAction(action)) {
        // Unwrap batch actions and validate sub-actions individually.
        // For known batch patterns (e.g. applyActionRecords + mergeDocumentData during remote sync),
        // only validate the applyActionRecords sub-action which carries the required flags.
        // For unrecognized batch patterns, validate all document sub-actions to catch missing flags.
        const hasApplyActionRecords = action.payload.some((sub) => sub.type === "applyActionRecords");
        for (const subAction of action.payload) {
            if (hasApplyActionRecords && subAction.type !== "applyActionRecords") {
                continue;
            }
            validateDocumentActionFlags(subAction, paths);
        }
        return;
    }

    validateDocumentActionFlags(action, paths);
};

function validateDocumentActionFlags(action: AnyAction, paths: (string | number)[][]) {
    if (!isDocumentAction(action)) {
        return;
    }
    if (!("shouldGenerateActionRecord" in action.payload)) {
        FluxLogger.captureError(
            new Error(
                `Action ${action.type} resulted in a change in the document without specifiying shouldGenerateActionRecord from paths: ${paths}`,
            ),
        );
    }
    if (!("updatesDocumentTimestamp" in action.payload)) {
        FluxLogger.captureError(
            new Error(
                `Action ${action.type} resulted in a change in the document without specifiying updatesDocumentTimestamp from paths: ${paths}`,
            ),
        );
    }
    if (!("canUndo" in action.payload)) {
        FluxLogger.captureError(
            new Error(
                `Action ${action.type} resulted in a change in the document without specifiying canUndo from paths: ${paths}`,
            ),
        );
    }
}

/**
 * Patch web workers state with the latest patch.
 * NOTE: this needs to happen before baking!
 */
export const syncPatchesWithWebWorkers: PatchHandlerSideEffectWithServices =
    (services: FluxServices) =>
    ({patch}) => {
        const task = async () => {
            try {
                // NOTE: web workers do not exist in jest
                if (!areWebWorkersSupported()) {
                    return;
                }

                const {userCodeRuntimeWrapper, useFeatureFlagsStore, appStore} = services;
                try {
                    // Filter out preview patches - worker doesn't need preview metadata,
                    // it already receives actual node changes via pcbLayoutNodes patches
                    const patchesForWorker = patch.forward.filter((p) => p.path[0] !== "preview");
                    await applyPcbLayoutEnginePatches(patchesForWorker);
                } catch (error) {
                    const message = "Error syncing PCB data!";
                    FluxLogger.captureError(error);
                    enqueueSyncErrorNotification(
                        message,
                        useFeatureFlagsStore.getState().showSyncErrors ?? true,
                        appStore,
                    );
                }

                // HACK: the userCodeRuntime is only erroring in storybook, and I can't forsee any need for it
                if (areWeInStorybook()) {
                    // eslint-disable-next-line no-console
                    console.warn("Ignoring update of user code runtime while in storybook");
                    return;
                }

                try {
                    const simulator = await userCodeRuntimeWrapper.init();
                    const filteredPatch = {
                        ...patch,
                        forward: patch.forward.filter((p) => p.path[0] !== "preview"),
                    };
                    await simulator.applyPatches(filteredPatch);
                } catch (error) {
                    // Any error should be captured by the user code runtime, which will handle updating the error state
                    FluxLogger.captureError(error);
                }
            } catch (err) {
                // Extra safe guard here - since we want to make sure bakingQueue never ends up in a rejected state
                FluxLogger.captureError(err);
            }
        };

        if (isPcbControlledBakingEnabled(services)) {
            bakingQueue = bakingQueue.then(task);
        } else {
            return task(); // Note the return; it's a subtlety that allows baking logic to remain testable, without a larger test suite refactor being necessary.
            // For now, testing assumes and execlusivly runs, the non-queued baking code path. Eventually, when the bake que becomes a perminant
            // feature;
            // - this return path will be removed,
            // - testing will work with the bake queue,
            // - bake queue logic will be verified in tests also
        }
    };

/**
 * Handle changes that require rebaking PCB nodes
 */
export const bakePcbNodes: PatchHandlerSideEffectWithServices =
    (services: FluxServices) =>
    ({action, document}) => {
        const task = async () => {
            try {
                // NOTE: Baking is called specially in the doc loader epic
                // AND `setDocumentData` action is already excluded
                // from withHistoryUpdate in the document reducer
                await bakeNodesFromLatestPatchWithRetry(action, document, services);
            } catch (error) {
                const message = "Error generating PCB data!";
                if (error instanceof Error) {
                    error.message = action.type + ": " + message + " " + error.message;
                }

                FluxLogger.captureError(error);
                enqueueSyncErrorNotification(
                    message,
                    services.useFeatureFlagsStore.getState().showSyncErrors ?? true,
                    services.appStore,
                );
            }
        };

        if (isPcbControlledBakingEnabled(services)) {
            bakingQueue = bakingQueue.then(task);
        } else {
            return task(); // Note the return; it's a subtlety that allows baking logic to remain testable, without a larger test suite refactor being necessary.
            // For now, testing assumes and execlusivly runs, the non-queued baking code path. Eventually, when the bake que becomes a perminant
            // feature;
            // - this return path will be removed,
            // - testing will work with the bake queue,
            // - bake queue logic will be verified in tests also
        }
    };

/**
 * Refetches sub-document data when the part version data cache of an element
 * changes, which is expected to happen when:
 *
 * - A part version is updated in a project
 * - A new element is added to the canvas
 * - An element is removed from the canvas
 *
 * NOTE: We assume that changes to elements also catches changes to other
 * subDocumentData collections such as routeVertices.
 */
export const maybeRefetchSubDocumentData: PatchHandlerSideEffectWithServices =
    (services: FluxServices) =>
    ({document, patch}) => {
        const {documentStore} = services;

        const {subDocumentData} = document;

        if (!patch) {
            throw new Error("Missing patch");
        }
        if (!subDocumentData) {
            throw new Error("Missing subDocumentData");
        }
        const {elements: elementsChanged} = getMatchingCollectionChangesFromPatch(
            patch,
            "elements",
            undefined, // key line
            "forward",
        );
        const {elements: elementsHavingPartVersionChanged} = getMatchingCollectionChangesFromPatch(
            patch,
            "elements",
            "part_version_data_cache", // key line
            "forward",
        );
        if (!elementsChanged && !elementsHavingPartVersionChanged) {
            return; // common case
        }

        // First, delete subDocumentData for elements that were removed
        const elementUids = Object.entries(elementsChanged ?? {})
            .filter(([_elementUid, changeOp]) => changeOp === "remove")
            .map(([elementUid, _changeOp]) => elementUid);
        if (elementUids.length) {
            // HACK: use timeout here to queue the action after the current dispatch
            // TODO: figure out if dispatch from rxjs is ok at all or if we need to
            // return the action to the stream
            // Dispatch directly to Zustand (bypassing Redux middleware bridge)
            setTimeout(() => documentStore.getState().deleteSubDocumentData(elementUids), 0);
        }

        // Next, fetch subDocumentData for elements that were added or had their
        // part version replaced
        const elementsAdded = Object.entries(elementsChanged ?? {}).filter(
            ([_elementUid, changeOp]) => changeOp === "add",
        );
        const elementsHavingPartVersionReplaced = Object.entries(elementsHavingPartVersionChanged || {}).filter(
            ([_elementUid, changeOp]) => changeOp === "replace",
        );
        const descriptors = concat(elementsAdded, elementsHavingPartVersionReplaced).map(([elementUid, changeOp]) => {
            const element = document.elements[elementUid];
            if (!element) {
                throw new Error(`Element ${elementUid} from patch not found in document`);
            }
            if (
                isDevEnv() &&
                changeOp === "replace" &&
                !Object.keys(subDocumentData.elements).some((key) => key.startsWith(element.uid))
            ) {
                throw new Error(`subDocumentData for element being replaced ${element.uid} not found`);
            }
            const {document_import_uid, version, part_uid: partUid} = element.part_version_data_cache;
            // NOTE: some system parts still do not have backing documents... these
            // will returned undefined in refetchSubDocumentData below and don't
            // have sublayouts anyway
            // TODO: create documents for remaining system parts that do not have them
            if (!isSystemPart(partUid) && !document_import_uid) {
                throw new Error(`Missing document_import_uid for element ${element.uid}`);
            }
            return {
                documentUid: document_import_uid!,
                documentVersion: version,
                // NOTE: important that this value is synced with
                // StaticPartVersionSerde or any place that generates snapshots
                schemaTag: "v0",
                partUid,
                elementUid,
            };
        });

        if (descriptors.length) {
            refetchSubDocumentData(services, descriptors, documentStore);
        }

        return;
    };

/**
 * Re-fetch sub-trees of the global subDocumentData for the given elements
 * according to the given snapshot version descriptors.
 *
 * NOTE: This could be very fast depending on what snapshots are already cached
 * in memory.
 *
 * NOTE: This function will only log an error if a snapshot is not found, in
 * which case there will be stale data.
 */
async function refetchSubDocumentData(
    services: Pick<FluxServices, "snapshotLibrary" | "partStorage" | "documentStorage" | "logger">,
    descriptors: (DocumentSnapshotDescriptor & {partUid: string; elementUid: string})[],
    documentStore: import("modules/document/state/document").DocumentStore,
): Promise<void> {
    const {snapshotLibrary, partStorage, documentStorage, logger} = services;
    // Get the snapshot for each element.
    const snapshots = await Promise.all(
        descriptors.map(async (descriptor) => {
            try {
                let snapshot = await snapshotLibrary.get(descriptor);
                if (!snapshot) {
                    // NOTE: this logic mirrors DocumentStorage.getFallbackSnapshot
                    logger.warn(`Document snapshot not found, fetching latest version: ${JSON.stringify(descriptor)}`);
                    const part = await partStorage.getPartByUid(descriptor.partUid);
                    if (!part) {
                        logger.error(`Document snapshot not found, part not found: ${JSON.stringify(descriptor)}`);
                        return undefined;
                    }
                    snapshot = await snapshotLibrary.get({
                        ...descriptor,
                        documentVersion: part.latest_version,
                    });
                    if (!snapshot) {
                        if (!isSystemPart(descriptor.partUid)) {
                            logger.error(
                                `Document snapshot not found, latest version not found: ${JSON.stringify(descriptor)}`,
                            );
                        }
                        return undefined;
                    }
                }
                return [descriptor.elementUid, snapshot] as const;
            } catch (error) {
                logger.error(`Error refetching document snapshot: ${JSON.stringify(descriptor)}`, error);
                return undefined;
            }
        }),
    );
    // Get the subDocumentData for each snapshot (which also includes the data from the given snapshot)
    const subDocumentDatas = await Promise.all(
        compact(snapshots).map(([elementUid, snapshot]) => documentStorage.getAllSubDocumentData(snapshot, elementUid)),
    );
    // Update the subDocumentData in the global store
    documentStore.getState().mergeSubDocumentData(subDocumentDatas);
}

/**
 * If we're not processing the action further, notify the ActionBakeObserver listeners
 *
 * This needs to be done here, otherwise actions that are dropped will never be baked,
 * the subscribers will forever wait for the action to be baked.
 */
export const maybeNotifyActionBakeObserverSubscribers =
    (services: FluxServices) =>
    ({action, shouldProcess}: NotifyActionBakeObserverSubscribersPayload) => {
        if (!shouldProcess) {
            // It can be argued the we should send `false` instead of `true` here,
            // but we've not encountered an error per se, so its ok
            services.actionBakeObserver.notify(action, true);
            services.actionBakeObserver.notifyDrcCompletion(action);
        }
    };
